// Utility type to extract the non-partial type
export type NonPartial<T> = {
    [P in keyof T]: T[P] extends Partial<infer U> ? U : T[P]
}

// Utility type to make all properties of a type mandatory
export type MakeRequired<T> = {
    [P in keyof T]-?: T[P]
}

/**
 * Mithril prop type - a function that can be called with no arguments to get the value,
 * or with one argument to set the value.
 */
export interface MithrilProp<T> {
    (): T
    (value: T): T
}

/**
 * All the valid events defined in Plausible for the portal.
 */
export enum PlausibleEvent {
    /** A user tries to authenticate to the portal; indicates possible accessibility issues */
    ACCESS = 'access',
    /** A user signs up for a campaign */
    CAMPAIGN_SIGNUP = 'campaign_signup',
    /** A user unsubscribes from a campaign */
    CAMPAIGN_UNSUBSCRIBE = 'campaign_unsubscribe',
    /** A user adds an item to their cart */
    CART_ADD = 'cart_add',
    /** A user decreases the quantity of an item in their cart */
    CART_DECREASE = 'cart_decrease',
    /** A user increases the quantity of an item in their cart */
    CART_INCREASE = 'cart_increase',
    /** A user removes an item from their cart */
    CART_REMOVE = 'cart_remove',
    /** A user places an order */
    ORDER = 'order',
    /** The order is rejected and returned to the cart validation step */
    ORDER_INVALID = 'order_invalid',
    /** A user downloads a pricelist */
    PRICELIST_DOWNLOAD = 'pricelist_download',
    /** A lead is registering for a portal account */
    REGISTRATION_REQUEST = 'registration_request',
    /** A user visits the portal */
    VISIT = 'visit',
}

interface Revenue {
    amount: number
    currency: 'EUR' | 'GBP' | 'USD' | 'JPY'
}

export enum PlausibleProps {
    ALCOHOL = 'alcohol',
    CASES = 'cases',
    CAMPAIGN_NAME = 'campaign_name',
    CAMPAIGN_UNSUBSCRIBE_REASON = 'campaign_unsubscribe_reason',
    COMPANY_TYPE = 'company_type',
    COUNTRY = 'country',
    CUSTOMS_STATUS = 'customs_status',
    LANGUAGE = 'language',
    CURRENCY = 'currency',
    PRODUCT_CATEGORY = 'product_category',
    PRODUCT_NAME = 'product_name',
    PRICE_PREFERENCE = 'price_preference',
    STEP = 'step',
    TYPE = 'type',
    SOURCE = 'source',
    VOLUME = 'volume',
}

export interface PlausibleEvents {
    [PlausibleEvent.ACCESS]: {
        props: {
            [PlausibleProps.TYPE]: 'access_login' | 'access_login_fail' | 'access_login_otp'
        }
    }

    [PlausibleEvent.CAMPAIGN_SIGNUP]: {
        props: {
            [PlausibleProps.CAMPAIGN_NAME]: string
        }
    }
    [PlausibleEvent.CAMPAIGN_UNSUBSCRIBE]: {
        props: {
            [PlausibleProps.CAMPAIGN_NAME]: string
            [PlausibleProps.CAMPAIGN_UNSUBSCRIBE_REASON]: string
        }
    }

    [PlausibleEvent.CART_ADD]: {
        props: {
            [PlausibleProps.ALCOHOL]: number
            [PlausibleProps.CASES]: number
            [PlausibleProps.COMPANY_TYPE]: string
            [PlausibleProps.CUSTOMS_STATUS]: string
            [PlausibleProps.PRODUCT_CATEGORY]: string
            [PlausibleProps.PRODUCT_NAME]: string
            [PlausibleProps.SOURCE]: string
            [PlausibleProps.VOLUME]: number
        }
        revenue: Revenue
    }

    [PlausibleEvent.CART_DECREASE]: {
        props: {
            [PlausibleProps.ALCOHOL]: number
            [PlausibleProps.CASES]: number
            [PlausibleProps.COMPANY_TYPE]: string
            [PlausibleProps.CUSTOMS_STATUS]: string
            [PlausibleProps.PRODUCT_CATEGORY]: string
            [PlausibleProps.PRODUCT_NAME]: string
            [PlausibleProps.SOURCE]: string
            [PlausibleProps.VOLUME]: number
        }
        revenue: Revenue
    }

    [PlausibleEvent.CART_INCREASE]: {
        props: {
            [PlausibleProps.ALCOHOL]: number
            [PlausibleProps.CASES]: number
            [PlausibleProps.COMPANY_TYPE]: string
            [PlausibleProps.CUSTOMS_STATUS]: string
            [PlausibleProps.PRODUCT_CATEGORY]: string
            [PlausibleProps.PRODUCT_NAME]: string
            [PlausibleProps.SOURCE]: string
            [PlausibleProps.VOLUME]: number
        }
        revenue: Revenue
    }

    [PlausibleEvent.CART_REMOVE]: {
        props: {
            [PlausibleProps.ALCOHOL]: number
            [PlausibleProps.CASES]: number
            [PlausibleProps.COMPANY_TYPE]: string
            [PlausibleProps.CUSTOMS_STATUS]: string
            [PlausibleProps.PRODUCT_CATEGORY]: string
            [PlausibleProps.PRODUCT_NAME]: string
            [PlausibleProps.SOURCE]: string
            [PlausibleProps.VOLUME]: number
        }
        revenue: Revenue
    }
    [PlausibleEvent.ORDER]: {
        props: {
            [PlausibleProps.COMPANY_TYPE]: string
            [PlausibleProps.CURRENCY]: string
            [PlausibleProps.CUSTOMS_STATUS]: string
            [PlausibleProps.LANGUAGE]: string
            [PlausibleProps.PRICE_PREFERENCE]: string
            [PlausibleProps.STEP]: string
        }
        revenue: Revenue
    }
    [PlausibleEvent.PRICELIST_DOWNLOAD]: {
        props: {
            [PlausibleProps.COMPANY_TYPE]: string
            [PlausibleProps.CURRENCY]: string
            [PlausibleProps.CUSTOMS_STATUS]: string
            [PlausibleProps.LANGUAGE]: string
            [PlausibleProps.PRICE_PREFERENCE]: string
        }
    }
    [PlausibleEvent.REGISTRATION_REQUEST]: {
        props: {
            [PlausibleProps.COUNTRY]: string
            [PlausibleProps.STEP]: string
        }
    }

    [PlausibleEvent.VISIT]: {
        props: {
            [PlausibleProps.COMPANY_TYPE]: string
            [PlausibleProps.CURRENCY]: string
            [PlausibleProps.CUSTOMS_STATUS]: string
            [PlausibleProps.LANGUAGE]: string
            [PlausibleProps.PRICE_PREFERENCE]: string
        }
    }
}
